import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowRight, PhoneCall, Sparkles, TrendingUp, ShieldCheck } from "lucide-react";
import { scrollToSection } from "@/App";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay, className = "" }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.95, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onRequestCall }) {
  const sectionRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 16 });
  const sy = useSpring(my, { stiffness: 55, damping: 16 });
  const arcX = useTransform(sx, (v) => v * 28);
  const arcY = useTransform(sy, (v) => v * 20);
  const imgX = useTransform(sx, (v) => v * -14);
  const imgY = useTransform(sy, (v) => v * -10);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const imgScrollY = useTransform(scrollYProgress, [0, 1], [0, 70]);

  const onMouseMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      id="top"
      ref={sectionRef}
      onMouseMove={onMouseMove}
      className="relative overflow-hidden bg-gradient-to-b from-white via-[#F5F9FF] to-[#EAF1FC]"
    >
      <div className="noise-overlay" />
      <div className="pointer-events-none absolute -top-40 right-[-10%] h-[36rem] w-[36rem] rounded-full bg-[#DCE9FC] blur-[120px]" />
      <div className="pointer-events-none absolute bottom-[-25%] left-[-10%] h-[28rem] w-[28rem] rounded-full bg-[#FDF1E3] opacity-70 blur-[120px]" />

      <motion.div
        style={{ x: arcX, y: arcY }}
        className="pointer-events-none absolute right-[2%] top-[12%] hidden xl:block"
        aria-hidden="true"
      >
        <svg width="460" height="320" viewBox="0 0 460 320" fill="none">
          <motion.path
            d="M20 285 C 150 240, 280 170, 430 40"
            stroke="#2E6BE6"
            strokeWidth="9"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.8, delay: 0.5, ease: EASE }}
          />
          <motion.polygon
            points="430,40 397,45 423,74"
            fill="#2E6BE6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.1, duration: 0.4 }}
          />
          <motion.circle cx="82" cy="251" r="17" fill="#E8862A" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1, duration: 0.5 }} />
          <motion.circle cx="238" cy="181" r="13" fill="#FFFFFF" stroke="#E3E9F2" strokeWidth="2" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 0.5 }} />
          <motion.circle cx="406" cy="56" r="15" fill="#2E9E44" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9, duration: 0.5 }} />
        </svg>
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl gap-14 px-4 pb-16 pt-32 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:pb-20 lg:pt-40">
        <div>
          <motion.span
            data-testid="hero-eyebrow-badge"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#C7DAF5] bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-[#0E2A6B] backdrop-blur"
          >
            <Sparkles className="h-3.5 w-3.5" />
            CSR Fundraising Advisory for NGOs
          </motion.span>

          <h1
            data-testid="hero-main-heading"
            className="mt-7 text-5xl font-extrabold uppercase leading-[0.98] tracking-tight text-[#0E2A6B] sm:text-6xl lg:text-7xl"
          >
            <MaskedLine delay={0.15}>From Intent</MaskedLine>
            <MaskedLine delay={0.32} className="font-display normal-case italic tracking-normal text-[#E8862A]">
              to Impact.
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheading-text"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7, ease: EASE }}
            className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            We bridge grassroots NGOs with corporate CSR capital — compliance-ready dossiers, the
            right CSR mandates, and boardroom representation until the grant is disbursed.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <motion.button
              whileHover={{ y: -3, scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              data-testid="hero-cta-explore-plans"
              onClick={() => scrollToSection("plans")}
              className="inline-flex items-center gap-2 rounded-full bg-[#0E2A6B] px-7 py-3.5 text-sm font-semibold text-white shadow-[0_12px_40px_-10px_rgba(14,42,107,0.45)] transition-colors hover:bg-[#0A1F50]"
            >
              Explore Association Plans
              <ArrowRight className="h-4 w-4" />
            </motion.button>
            <motion.button
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
              data-testid="hero-cta-request-call"
              onClick={onRequestCall}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#E8862A] bg-white/70 px-7 py-3.5 text-sm font-semibold text-[#C96F1B] backdrop-blur transition-colors hover:bg-[#E8862A] hover:text-white"
            >
              <PhoneCall className="h-4 w-4" />
              Schedule a Strategy Call
            </motion.button>
          </motion.div>

          <motion.dl
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-14 grid max-w-xl grid-cols-1 gap-5 border-t border-[#DCE5F2] pt-8 sm:grid-cols-3"
          >
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Reach</dt>
              <dd data-testid="hero-stat-funds-raised" className="mt-1 text-lg font-extrabold text-[#0E2A6B] sm:text-xl">
                Pan-India CSR Network
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Approach</dt>
              <dd data-testid="hero-stat-ngos-supported" className="mt-1 text-lg font-extrabold text-[#0E2A6B] sm:text-xl">
                End-to-End Compliance
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-slate-500">Commitment</dt>
              <dd data-testid="hero-stat-success-rate" className="mt-1 text-lg font-extrabold text-[#E8862A] sm:text-xl">
                Success-Linked Engagement
              </dd>
            </div>
          </motion.dl>
        </div>

        <motion.div
          style={{ x: imgX, y: imgY }}
          initial={{ opacity: 0, scale: 0.94, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.55, ease: EASE }}
          className="relative hidden lg:block"
        >
          <div
            className="relative h-[540px] overflow-hidden shadow-2xl"
            style={{ clipPath: "polygon(0 0, 100% 4%, 100% 100%, 0 96%)" }}
          >
            <motion.img
              style={{ y: imgScrollY }}
              src="/assets/hero-field.png"
              alt="Impact Sutra field worker engaging with a rural community during an NGO program"
              className="h-full w-full scale-110 object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0E2A6B]/40 via-transparent to-transparent" />
            <div className="pointer-events-none absolute -left-10 top-0 h-64 w-64 rounded-full bg-white/20 blur-[80px]" />
          </div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.2, ease: EASE }}
            className="absolute -left-6 top-10 rounded-2xl border border-white/60 bg-white/85 px-4 py-3 shadow-xl backdrop-blur-md"
          >
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
              <TrendingUp className="h-4 w-4 text-[#E8862A]" />
              Active CSR Mandates
            </div>
            <p className="font-mono-alt mt-1 text-lg font-semibold text-slate-900">Proposals in review</p>
            <p className="text-xs text-slate-500">Across education, health & rural sectors</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.35, ease: EASE }}
            className="absolute -bottom-6 right-4 rounded-2xl border border-white/60 bg-white/85 px-4 py-3 shadow-xl backdrop-blur-md"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#2E9E44]" />
              <div>
                <p className="text-sm font-bold text-slate-900">Audit-Ready Dossiers</p>
                <p className="text-xs text-slate-500">CSR-1 · 80G · 12A documentation support</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.7, duration: 0.8 }}
        className="relative mx-auto flex max-w-7xl justify-center pb-10"
      >
        <div className="flex flex-col items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.35em] text-slate-400">
          Scroll
          <motion.span
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            className="h-8 w-px bg-gradient-to-b from-[#E8862A] to-transparent"
          />
        </div>
      </motion.div>
    </section>
  );
}
